import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useParams } from 'react-router-dom';
import { getCurrentCycleContentById } from '../../services/admin/admin-current-cycle-content.service';
import styled from 'styled-components';
import Variables from '../../design-system/variables';
import breakpoints from '../../design-system/breakpoints';
import OvBackButton from '../UI/atoms/OvBackButton';
import { useTranslation } from 'react-i18next';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvTable from '../UI/molecules/OvTable';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { CurrentCycleContentDataUtils } from '../../common/utils/services/current-cycle-content-data-utils';
import { useAdminCurrentCycleContentMutations } from '../../hooks/use-admin-current-cycle-content-mutations';

const CurrentCycleContentDetails = () => {
  const { t } = useTranslation();
  const { currentCycleContentId }: any = useParams();

  const {
    data: currentCycleContent,
    isPending: isGetCurrentCycleContentPending,
    isFetching: isGetCurrentCycleContentFetching,
    error: getCurrentCycleContentError,
  } = useQuery({
    queryKey: ['current-cycle-content', currentCycleContentId],
    queryFn: () => getCurrentCycleContentById(currentCycleContentId),
    retry: 3,
    enabled: !!currentCycleContentId,
  });

  const { updateCurrentCycleContent } = useAdminCurrentCycleContentMutations(
    currentCycleContentId!
  );

  const isLoading =
    isGetCurrentCycleContentFetching ||
    isGetCurrentCycleContentPending ||
    updateCurrentCycleContent.isPending;

  const allErrors = [
    getCurrentCycleContentError,
    updateCurrentCycleContent.error,
  ].filter((e): e is Error => !!e);

  return (
    <DetailPageWrapper>
      <DetailsHeader>
        <DetailsWrapper>
          <OvBackButton label={t('common.actions.back')} />
          {`${
            currentCycleContent?.current_phase
          } (${CurrentCycleContentDataUtils.getAgeRange(currentCycleContent)})`}
        </DetailsWrapper>

        <span>
          {allErrors.map((err, i) => (
            <OvErrorMessage key={i} message={err.message} />
          ))}
        </span>
      </DetailsHeader>

      <DetailPageContentWrapper>
        <TableWrapper>
          <OvTable
            data={CurrentCycleContentDataUtils.mapCurrentCycleContentToTableData(
              currentCycleContent
            )}
            onSaveRowValue={updateCurrentCycleContent.mutate}
            editable={true}
          />
        </TableWrapper>
      </DetailPageContentWrapper>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </DetailPageWrapper>
  );
};

export default CurrentCycleContentDetails;

const DetailPageWrapper = styled.div`
  margin-left: 1rem;
  border-radius: ${Variables.borderRadius.SMALL};
`;

const DetailsHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  font-weight: bold;
`;

const DetailsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const DetailPageContentWrapper = styled.div`
  display: flex;
  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
  }
`;

const TableWrapper = styled.div`
  flex-grow: 1;
  align-self: flex-start;
  margin-left: 12px;
  width: 100%;

  @media (max-width: ${breakpoints.md}) {
    margin-left: 0;
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;
